import { forwardRef, type AnchorHTMLAttributes, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Link } from 'react-router';
import { Icon, Spinner, type IconName } from './icons';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'danger-outline' | 'amber' | 'tint' | 'dashed' | 'dark';
export type ButtonSize = 'sm' | 'md' | 'lg';
type Look = { variant?: ButtonVariant; size?: ButtonSize; icon?: IconName; iconRight?: IconName; block?: boolean };

const cls = ({ variant = 'primary', size = 'md', block }: Look, extra = '') => ['btn', variant, size === 'md' ? '' : size, block ? 'block' : '', extra].filter(Boolean).join(' ');
const inner = (children: ReactNode, icon?: IconName, iconRight?: IconName, size?: ButtonSize) => (
  <>
    {icon && <Icon name={icon} size={size === 'sm' ? 16 : 18} strokeWidth={1.8} />}
    {children}
    {iconRight && <Icon name={iconRight} size={size === 'sm' ? 16 : 18} strokeWidth={1.8} className={iconRight === 'arrowRight' ? 'arrow' : undefined} />}
  </>
);

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & Look & {
  loading?: boolean;
  /** Text while loading (progressive form, e.g. "Wird erstellt …"); defaults to the normal label. */
  loadingLabel?: ReactNode;
};

/** Button (docs/08): primary · secondary · ghost · danger · amber (+ tint, dashed, dark, danger-outline); loading keeps the width and sets aria-busy. */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button({ variant = 'primary', size = 'md', loading, loadingLabel, icon, iconRight, block, children, disabled, className = '', type = 'button', ...rest }, ref) {
  return (
    <button ref={ref} type={type} className={cls({ variant, size, block }, className)} disabled={disabled || loading} aria-busy={loading || undefined} {...rest}>
      {loading ? <><Spinner size={size === 'sm' ? 16 : 18} />{loadingLabel ?? children}</> : inner(children, icon, iconRight, size)}
    </button>
  );
});

/** Router link styled as a button. `href` (instead of `to`) renders a plain anchor for external targets. */
export function LinkButton({ to, href, state, variant = 'primary', size = 'md', icon, iconRight, block, children, className = '', ...rest }: Look & { to?: string; href?: string; state?: unknown; children: ReactNode; className?: string } & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>) {
  const c = cls({ variant, size, block }, className);
  if (href !== undefined || to === undefined) return <a className={c} href={href} {...rest}>{inner(children, icon, iconRight, size)}</a>;
  return <Link className={c} to={to} state={state} {...rest}>{inner(children, icon, iconRight, size)}</Link>;
}

/** Square icon-only button; `label` is required and becomes aria-label + tooltip. */
export const IconButton = forwardRef<HTMLButtonElement, Omit<ButtonProps, 'children' | 'icon' | 'iconRight'> & { icon: IconName; label: string }>(function IconButton({ icon, label, variant = 'ghost', size = 'md', className = '', loading, type = 'button', ...rest }, ref) {
  return (
    <button ref={ref} type={type} className={cls({ variant, size }, `icon ${className}`)} aria-label={label} title={label} aria-busy={loading || undefined} disabled={rest.disabled || loading} {...rest}>
      {loading ? <Spinner size={18} /> : <Icon name={icon} size={size === 'sm' ? 16 : 20} strokeWidth={1.8} />}
    </button>
  );
});
